import { Component, ElementRef, OnInit, ViewChild } from '@angular/core';
import { firstValueFrom } from 'rxjs';
import { FormBuilder, FormControl, FormGroup, Validators } from '@angular/forms';
import { Location } from '@angular/common';
import { Router } from '@angular/router';
import { PlantsService } from '../../company/data/plants.service';
import { ProductionReceiptsService } from '../data/production-receipts.service';
import { CustomerService } from '../../customers/data/customer.service';
import { RecipesService } from '../../recipes/data/recipes.service';
import { TrucksService } from '../../logistics/data/trucks.service';
import { DriversService } from '../../logistics/data/drivers.service';
import { Truck } from '../../logistics/data/interfaces/truck';
import { Driver } from '../../logistics/data/interfaces/driver';
import { TokenService } from '../../../services/auth/token.service';
import { TranslateService } from '../../../services/multilingual/translate.service';
import { DatetimeService } from '../../../services/datetime/datetime.service';
import { ValidationPatternsService } from '../../../services/validation/patterns.service';
import { ModalService } from 'src/app/components/JBM/JBMModal/service/modal.service';
import { JBMToastsService } from '../../../components/JBM/Views/JBMToasts/JBMToasts.service';
import { JBMSelectOption } from '../../../components/JBM/Forms/JBMFormGroups/JBMSelect/JBMSelect.component';
import { GroupRights } from '../../users/data/interfaces/grouprights';
import { setInputFocus } from 'src/app/utilities/HTMLElement';
import { TruckSelectorComponent } from 'src/app/components/domain/selectors/truck/truck.selector.component';
import { DriverSelectorComponent } from 'src/app/components/domain/selectors/driver/driver.selector.component';
import { toInteger, toFloat } from 'src/app/utilities/valueConversions';

@Component({
   selector: 'manual-receipt-entry',
   templateUrl: './manual-receipt-entry.component.html',
   styleUrls: ['./manual-receipt-entry.component.scss']
})
export class ManualReceiptEntryComponent implements OnInit {
   form: FormGroup;
   editable: boolean = false;
   isSubmitted: boolean = false;
   rights: GroupRights;
   language: string = this.TokenService.getLanguage();
   plants: any[] = []
   plantOptions: JBMSelectOption[]=[];

   project: any = null
   customer: any = null
   order: any = null
   remainingAmount: number = 0

   recipe: any = null
   plant_id: number = 0
   truck: Truck = this.TrucksService.getEmpty()
   driver: Driver = this.DriversService.getEmpty()
   environment_id: number = 0;

   error: string = '';

   @ViewChild('modalReceipt') modalReceipt: ElementRef;
   @ViewChild('modalProjectDetails') modalProjectDetails: ElementRef;
   @ViewChild('modalCustomerDetails') modalCustomerDetails: ElementRef;
   @ViewChild('modalOrderDetails') modalOrderDetails: ElementRef;
   @ViewChild('modalRecipeDetails') modalRecipeDetails: ElementRef;
   @ViewChild('truckSelector') truckSelector: TruckSelectorComponent;
   @ViewChild('driverSelector') driverSelector: DriverSelectorComponent;

   constructor(
      private Router: Router,
      private Location: Location,
      private formBuilder: FormBuilder,
      private PlantsService: PlantsService,
      private ProductionReceiptsService: ProductionReceiptsService,
      private CustomerService: CustomerService,
      private RecipesService: RecipesService,
      private TrucksService: TrucksService,
      private DriversService: DriversService,
      private TokenService: TokenService,
      private TranslateService: TranslateService,
      private DatetimeService: DatetimeService,
      private ValidationPatternsService: ValidationPatternsService,
      private ModalService: ModalService,
      private JBMToastsService: JBMToastsService
   ) {
      this.rights = this.TokenService.getRightsByName('production');

      this.addControls();
   }
   ngOnInit(): void {
      this.PlantsService.getData().subscribe({
        next: data => {
          this.plants = data.data
          this.plantOptions=this.PlantsService.getSelectOptions(this.plants);
        },
        error: error => console.error(error)
      })

      this.setDefaultValues();

     setTimeout(() => this.ModalService.open(this.modalReceipt, 'receipt'), 100);
     setInputFocus('projects_id', true, 500)
   }

   addControls() {
      this.form = this.formBuilder.group({
        plants_id: new FormControl('', [Validators.required]),
        receipts_id: new FormControl('', [Validators.required, Validators.pattern(/^[0-9]+$/)]),
        loading_time: new FormControl('', [Validators.required, Validators.pattern(this.DatetimeService.getTimeRegEx())]),
        leave_plant_time: new FormControl('', [Validators.required, Validators.pattern(this.DatetimeService.getTimeRegEx())]),
        trucks_id: new FormControl(''),
        drivers_id: new FormControl(''),
        amount: new FormControl('', [Validators.required, Validators.pattern(this.ValidationPatternsService.floatPattern(2))]),
        receipt_amount: new FormControl('', [Validators.required, Validators.pattern(this.ValidationPatternsService.floatPattern(2))]),
        execute_date: new FormControl('', [this.DatetimeService.dateValidator(this.DatetimeService)]),
        external_id: new FormControl('', [Validators.pattern(/^[0-9]+$/)]),
        comment: new FormControl('')
      }, { updateOn: 'blur' });
   }
   get formControls() {
      return this.form.controls;
   }
   setDefaultValues() {
      let now = new Date();
      this.formControls.execute_date.setValue(this.DatetimeService.getDateToDMY(now));
      this.formControls.loading_time.setValue(this.DatetimeService.getDateToTimeStr(now));
      this.formControls.leave_plant_time.setValue(this.DatetimeService.getDateToTimeStr(now));
   }

   // Selections

   selectProject(project: any) {
      this.order = null
      this.resetPlant()
      if(!project.id) return

      this.project = project
      this.setPlant(project.plants_id)

      this.CustomerService.get(this.project.customer_id).subscribe(
         (data) => this.customer = data.data,
         (error) => console.error(error)
      )
   }
   selectOrder(order: any) {
      if(this.order && this.order.plants_id)
         this.resetPlant()
      this.order = order

      if (order.order_plants_id)
         // Order plant overrules project plant
         this.setPlant(order.order_plants_id)

      this.ProductionReceiptsService.calcRemainingAmount(this.order.id).subscribe(
        (data) => {
          this.remainingAmount = data.data
        },
        (error) => console.error(error)
      )

      this.RecipesService.getOne(this.plant_id, this.order.recipes_id).subscribe(
         (data) => this.recipe = data.data,
         (error) => console.error(error)
      )
   }
   selectPlant(event: any) {
     this.setPlant(parseInt(event.target.value))
   }
   selectTruck(truck: any) {
      this.truck = truck
      this.formControls.trucks_id.setValue(truck.id.toString());

      if (truck && truck.capacity) {
         let amount = truck.capacity.toString().replace('.', ',');
         this.formControls.amount.setValue(amount);
         this.formControls.receipt_amount.setValue(amount);
      }
   }
   selectDriver(driver: any) {
      this.driver = driver
      this.formControls.drivers_id.setValue(driver.id.toString());
   }

   setPlant(id: number) {
      this.plant_id = id
      this.editable = this.order && this.order.id;
      this.formControls.plants_id.setValue(this.plant_id.toString());

      this.ProductionReceiptsService.calcNewId(this.plant_id).subscribe(
        (data) => this.formControls.receipts_id.setValue(data.data.toString()),
        (error) => console.error(error)
      );
   }
   resetPlant() {
      this.plant_id = 0
      this.editable = false;
      this.formControls.plants_id.setValue(this.plant_id.toString());
      this.formControls.receipts_id.setValue('');
   }

   // Detail dialogs

   showProject() {
      this.ModalService.open(this.modalProjectDetails, 'projectDetails');
   }
   closeProject() {
      this.ModalService.close('projectDetails');
   }
   showCustomer() {
      this.ModalService.open(this.modalCustomerDetails, 'customerDetails')
   }
   closeCustomer() {
      this.ModalService.close('customerDetails');
   }
   showOrder() {
      this.ModalService.open(this.modalOrderDetails, 'orderDetails')
   }
   closeOrder() {
      this.ModalService.close('orderDetails');
   }
   showRecipe() {
      this.ModalService.open(this.modalRecipeDetails, 'recipeDetails')
   }
   closeRecipe() {
      this.ModalService.close('recipeDetails');
   }

   getPlantName(id: number): string {
      let plant = this.plants.find(p => p.id === id)
      return plant ? plant.name : ''
   }

   async checkUnique(): Promise<boolean> {
      let number = parseInt(this.formControls.receipts_id.value.trim());
      if (isNaN(number) || !number) return true;

      this.formControls.receipts_id.setErrors(null);

      let result: { error: boolean, data?: boolean } = { error: false };
      try {
         let response = await firstValueFrom(
            this.ProductionReceiptsService.isNumberUnique(this.plant_id, number)
         );
         result.data = response.data;
      } catch (error) {
         result.error = true;
         console.error(error);
      }

      if (!result.error && result.data === false)
         this.formControls.receipts_id.setErrors({ not_unique: true });

      return !result.error;
   }


   copyAmountToReceiptAmount() {
      if (!this.formControls.receipt_amount.value)
         this.formControls.receipt_amount.setValue(this.formControls.amount.value);
   }

   save() {
      this.isSubmitted = true;
      this.error = '';

      if (!this.truck.id)
        this.truckSelector.setInvalid();
      if (!this.driver.id)
        this.driverSelector.setInvalid();

      let loading_time = this.getDateTime(this.formControls.execute_date.value, this.formControls.loading_time.value)
      let leave_plant_time = this.getDateTime(this.formControls.execute_date.value, this.formControls.leave_plant_time.value)
      if (loading_time > leave_plant_time)
        this.formControls.loading_time.setErrors({ 'to-late': true });

      if (this.form.invalid || !this.truck.id || !this.driver.id)
        return;

      let data = {
         projects_id: this.project.id,
         project: this.project.name,
         customers_id: this.project.customer_id,
         customer: this.customer.name,
         orders_id: this.order.id,
         order_amount: this.order.amount,
         recipes_id: this.recipe.id,
         plants_id: toInteger(this.formControls.plants_id.value, 0),
         receipts_id: toInteger(this.formControls.receipts_id.value, 0),
         loading_time: loading_time,
         leave_plant_time: leave_plant_time,
         amount: toFloat(this.formControls.amount.value, 0),
         receipt_amount: toFloat(this.formControls.receipt_amount.value, 0),
         trucks_id: this.truck.id,
         drivers_id: this.driver.id,
         execute_date: this.getDate(this.formControls.execute_date.value),
         comment: this.formControls.comment.value,
         manually: true
      };

      this.ProductionReceiptsService.create(data).subscribe(
         () => { },
         (error) => this.handleError(error),
         () => this.afterSave()
      );
   }

   handleError(error: any) {
      this.error = typeof error === 'string' ? error : '';
      if (!this.error)
         console.error(error);
   }

   afterSave() {
      this.JBMToastsService.success(this.TranslateService.GetTranslation('ui.entity-saved'));
      this.close();
   }

   close() {
     this.Router.navigate([{ outlets: { 'modal': null }} ] );
   }

   private getDate(date: string): number {
      let parts = this.DatetimeService.getDatePartsDMY(date);
      return new Date(parts.year, parts.month - 1, parts.day).getTime();
   }

   private getDateTime(date: string, time: string): number {
      let dateParts = this.DatetimeService.getDatePartsDMY(date);
      let timeParts = this.DatetimeService.getTimeParts(time);
      return new Date(dateParts.year, dateParts.month - 1, dateParts.day, timeParts.hours, timeParts.minutes, timeParts.seconds).getTime();
   }
}
